// theme.js
import ThemeError from "%/dynamic-component-editor/errors/theme-error";
import { links } from "%/dynamic-component-editor/enums/link-enums";
import BaseSingletonEntity from "%/dynamic-component-editor/classes/base-singleton-entity";
class Theme extends BaseSingletonEntity {
  /** ******************* Private Static Vars *********************/
  static #allowConstruction = false;
  /** ******************* Private Vars *********************/
  #store = null;
  #id = null;
  /** ******************* Public Static Methods *********************/
  static get(id) {
    const instanceKey = `theme-${id}`;
    return BaseSingletonEntity._getInstance(Theme, instanceKey, () => {
      Theme.#allowConstruction = true;
      const instance = new Theme(id);
      Theme.#allowConstruction = false;
      return instance;
    });
  }
  /** ******************* Public Getters *********************/
  get id() {
    return this.#id;
  }
  get version() {
    return this.#store.currentVersion;
  }
  get savingMessage() {
    return this.#store.savingMessage;
  }
  get apiV1() {
    return this.links[links.api];
  }
  // Raw theme JSONs as last seen by `process()`. Exposed so consumers can
  // read off this class instance instead of reaching into `useStyleStore`
  // or `useBrandingStore` for the same data. `process()` repopulates these
  // every time the App-engine Theme entity changes (the theme-bridge
  // watcher in Studio's main-vue.js calls `process()` reactively), so any
  // computed that reads these getters stays live.
  get configuration() {
    return this.#store.themeConfiguration ?? null;
  }
  get palette() {
    return this.#store.themePalette ?? null;
  }
  get semanticPalette() {
    return this.#store.themeSemanticPalette ?? null;
  }
  /** ******************* Public Setters *********************/
  set savingMessage(status) {
    this.#store.savingMessage = status;
  }
  set version(versionId) {
    this.#store.currentVersion = versionId;
  }
  /** ******************* Public Methods *********************/
  constructor(id) {
    super();
    if (!Theme.#allowConstruction) {
      throw new ThemeError("Use Theme.get() instead.", {});
    }
    this.#id = id;
    BaseSingletonEntity._callInitializeStore(this);
  }
  process(version, themeConfig, paletteConfig, semanticConfig) {
    const applyConfig = () => {
      let processedThemeConfig = this.#store.config;
      if (version !== this.#store.currentVersion) {
        processedThemeConfig = this.#processThemeConfig(
          themeConfig,
          paletteConfig,
          semanticConfig,
        );
      }
      this.#store.$patch({
        currentVersion: version,
        config: processedThemeConfig,
        themeConfiguration: themeConfig,
        themePalette: paletteConfig,
        themeSemanticPalette: semanticConfig,
        processed: true,
        hydrated: true,
      });
    };

    applyConfig();

    return this;
  }

  transformIntoThemeValues(value, themeMode = "light-mode") {
    if (value === null || value === undefined) {
      return value;
    }
    if (Array.isArray(value)) {
      return value.map(item => this.transformIntoThemeValues(item, themeMode));
    }
    if (typeof value === "object") {
      const { source, value: refValue } = value;
      if (source !== undefined && refValue !== undefined) {
        return this.#resolveToken(source, refValue, themeMode);
      }
      const resolved = {};
      for (const [k, v] of Object.entries(value)) {
        resolved[k] = this.transformIntoThemeValues(v, themeMode);
      }
      return resolved;
    }
    return value;
  }
  #resolveToken(source, value, themeMode) {
    if (source?.startsWith("custom.")) {
      return value ?? null;
    }

    if (source?.startsWith("semantic.")) {
      const key = `semantic.${themeMode}.${source.replace("semantic.", "")}.${value}`;
      return this.#store.config[key] ?? null;
    }

    const key = `${source}.${value}`;
    const resolved = this.#store.config[key];

    // If the stored value is a semantic pointer, recurse with correct mode
    if (resolved?.source && resolved?.value) {
      return this.#resolveToken(resolved.source, resolved.value, themeMode);
    }

    return resolved ?? null;
  }

  /** ******************* Private Methods *********************/
  _initializeStore() {
    const storeKey = `theme-${this.#id}`;
    const store = this._defineStore(storeKey, () => ({
      currentVersion: 0,
      savingMessage: "All changes saved",
      config: {},
      themeConfiguration: null,
      themePalette: null,
      themeSemanticPalette: null,
    }));
    this.#store = store;
  }

  #processThemeConfig(themeConfig, paletteConfig, semanticConfig) {

    return {
      ...this.#flattenThemeTokens(themeConfig, paletteConfig),
      ...this.#flattenPaletteColors(paletteConfig),
      ...this.#flattenPaletteSizes(paletteConfig),
      ...this.#flattenPaletteFonts(paletteConfig),
      ...this.#flattenSemanticColors(semanticConfig),
      __custom__: true,
    };
  }

  #flattenThemeTokens(themeConfig, paletteConfig) {
    const tokens = {};

    for (const [categoryKey, attributeGroup] of Object.entries(themeConfig)) {
      for (const [attributeKey, tokenGroup] of Object.entries(attributeGroup)) {
        for (const [tokenKey, tokenRef] of Object.entries(tokenGroup)) {
          tokens[`theme.${attributeKey}.${categoryKey}.${tokenKey}`] = this.#resolveTokenRef(attributeKey, tokenRef, paletteConfig);
        }
      }
    }

    return tokens;
  }

  #resolveTokenRef(attributeKey, tokenRef, paletteConfig) {
    if (typeof tokenRef !== "string" || !tokenRef.endsWith("]")) {
      return tokenRef;
    }

    const isPrimitive = tokenRef.startsWith("Primitives.");
    const isSemantic = tokenRef.startsWith("Semantic.");
    if (!isPrimitive && !isSemantic) {
      return tokenRef;
    }

    const parts = tokenRef.split(".");
    if (parts.length < 3) {
      return tokenRef;
    }

    const refType = parts[0];
    const paletteCategory = parts[1];
    const remainder = parts.slice(2).join(".");

    const bracketStart = remainder.indexOf("[");
    if (bracketStart === -1) {
      return tokenRef;
    }

    const subcategory = remainder.slice(0, bracketStart);
    const tokenKey = remainder.slice(bracketStart + 1, -1).replace(/['"]/g, "");

    if (refType === "Semantic") {
      // Store as a pointer instead of resolving eagerly
      return { source: `semantic.${attributeKey}`, value: `${subcategory}.${tokenKey}` };
    }

    if (refType === "Primitives") {
      const resolved = paletteConfig?.[paletteCategory]?.[subcategory]?.[tokenKey];
      return resolved !== undefined ? resolved : tokenKey;
    }

    return tokenRef;
  }

  #flattenPaletteColors(paletteConfig) {
    const COLOR_ROLE_ALIASES = ["background", "font", "border", "color", "icon", "gradient"];
    const tokens = {};

    for (const [colorGroup, shades] of Object.entries(paletteConfig.colors ?? {})) {
      for (const [shade, hexValue] of Object.entries(shades)) {
        for (const alias of COLOR_ROLE_ALIASES) {
          tokens[`palette.${alias}.${colorGroup}.${shade}`] = hexValue;
        }
      }
    }

    return tokens;
  }

  #flattenPaletteSizes(paletteConfig) {
    const tokens = {};

    for (const [sizeGroup, sizeTokens] of Object.entries(paletteConfig.sizes ?? {})) {
      for (const [tokenKey, value] of Object.entries(sizeTokens)) {
        tokens[`palette.size.${sizeGroup}.${tokenKey}`] = value;
      }
    }

    return tokens;
  }

  #flattenPaletteFonts(paletteConfig) {
    const tokens = {};

    for (const [fontGroup, fontTokens] of Object.entries(paletteConfig.fonts ?? {})) {
      for (const [tokenKey, value] of Object.entries(fontTokens)) {
        tokens[`palette.font.${fontGroup}.${tokenKey}`] = value;
      }
    }

    return tokens;
  }

  #flattenSemanticColors(semanticConfig) {
    const COLOR_ROLE_ALIASES = ["font", "background", "border", "icon", "gradient", "color"];
    const tokens = {};

    for (const [mode, colorGroups] of Object.entries(semanticConfig ?? {})) {
      if (typeof colorGroups !== "object" || Array.isArray(colorGroups)) {
        continue;
      }

      for (const [colorGroup, variants] of Object.entries(colorGroups)) {
        if (typeof variants !== "object" || Array.isArray(variants)) {
          continue;
        }

        for (const [variantKey, hexValue] of Object.entries(variants)) {
          for (const alias of COLOR_ROLE_ALIASES) {
            tokens[`semantic.${mode}.${alias}.${colorGroup}.${variantKey}`] = hexValue;
          }
        }
      }
    }

    return tokens;
  }
}
export default Theme;
